/**
 * Metadata Loader Service
 *
 * Handles loading, caching, and managing metadata files for the dynamic form system.
 * Supports templates, fields, datasets, and enrichment configurations.
 */

import { config } from "../config";

// Cache for loaded metadata
const metadataCache = new Map();

// Loading state tracking
const loadingStates = new Map();

// Error tracking
const errorStates = new Map();

/**
 * Cache configuration
 */
const CACHE_CONFIG = {
  ttl: 30 * 60 * 1000, // 30 minutes TTL
  maxSize: 100, // Maximum number of cached items
  enabled: config.features.enableMetadataCache,
};

/**
 * Metadata file paths configuration
 */
const METADATA_PATHS = {
  templates: "/metadata/templates",
  fields: "/metadata/fields",
  datasets: "/metadata/datasets",
  enrichments: "/metadata/enrichments",
};

/**
 * Cache entry structure
 */
class CacheEntry {
  constructor(data, timestamp = Date.now()) {
    this.data = data;
    this.timestamp = timestamp;
    this.lastAccessed = timestamp;
  }

  isExpired(ttl = CACHE_CONFIG.ttl) {
    return Date.now() - this.timestamp > ttl;
  }

  touch() {
    this.lastAccessed = Date.now();
  }
}

/**
 * Cache management utilities
 */
class MetadataCache {
  static set(key, data) {
    if (!CACHE_CONFIG.enabled) return;

    // Clean expired entries if cache is at capacity
    if (metadataCache.size >= CACHE_CONFIG.maxSize) {
      this.cleanup();
    }

    // Remove oldest entry if still at capacity
    if (metadataCache.size >= CACHE_CONFIG.maxSize) {
      const oldestKey = this.getOldestKey();
      if (oldestKey) {
        metadataCache.delete(oldestKey);
      }
    }

    metadataCache.set(key, new CacheEntry(data));
  }

  static get(key) {
    if (!CACHE_CONFIG.enabled) return null;

    const entry = metadataCache.get(key);
    if (!entry) return null;

    if (entry.isExpired()) {
      metadataCache.delete(key);
      return null;
    }

    entry.touch();
    return entry.data;
  }

  static has(key) {
    if (!CACHE_CONFIG.enabled) return false;

    const entry = metadataCache.get(key);
    if (!entry) return false;

    if (entry.isExpired()) {
      metadataCache.delete(key);
      return false;
    }

    return true;
  }

  static delete(key) {
    metadataCache.delete(key);
    loadingStates.delete(key);
    errorStates.delete(key);
  }

  static clear() {
    metadataCache.clear();
    loadingStates.clear();
    errorStates.clear();
  }

  static cleanup() {
    const now = Date.now();
    for (const [key, entry] of metadataCache.entries()) {
      if (entry.isExpired()) {
        metadataCache.delete(key);
      }
    }
  }

  static getOldestKey() {
    let oldestKey = null;
    let oldestTime = Date.now();

    for (const [key, entry] of metadataCache.entries()) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }

    return oldestKey;
  }

  static getStats() {
    const now = Date.now();
    let expired = 0;
    let total = metadataCache.size;

    for (const [, entry] of metadataCache.entries()) {
      if (entry.isExpired()) {
        expired++;
      }
    }

    return {
      total,
      expired,
      active: total - expired,
      capacity: CACHE_CONFIG.maxSize,
      enabled: CACHE_CONFIG.enabled,
    };
  }
}

/**
 * HTTP client with error handling and retry logic
 */
class MetadataHTTPClient {
  static async fetch(url, options = {}) {
    const defaultOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      timeout: 10000, // 10 second timeout
      ...options,
    };

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      defaultOptions.timeout
    );

    try {
      const response = await fetch(url, {
        ...defaultOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        throw new Error(`Request timeout: ${url}`);
      }

      throw error;
    }
  }

  static async fetchWithRetry(url, options = {}, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await this.fetch(url, options);
      } catch (error) {
        lastError = error;

        // Don't retry for client errors (4xx)
        if (error.message.includes("HTTP 4")) {
          throw error;
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError;
  }
}

/**
 * Loading state management
 */
class LoadingStateManager {
  static setLoading(key, isLoading) {
    if (isLoading) {
      loadingStates.set(key, Date.now());
    } else {
      loadingStates.delete(key);
    }
  }

  static isLoading(key) {
    return loadingStates.has(key);
  }

  static getLoadingStates() {
    const states = {};
    for (const [key] of loadingStates.entries()) {
      states[key] = true;
    }
    return states;
  }

  static clearExpiredLoading(timeout = 30000) {
    const now = Date.now();
    for (const [key, timestamp] of loadingStates.entries()) {
      if (now - timestamp > timeout) {
        loadingStates.delete(key);
      }
    }
  }
}

/**
 * Error state management
 */
class ErrorStateManager {
  static setError(key, error) {
    errorStates.set(key, {
      message: error.message,
      timestamp: Date.now(),
      stack: error.stack,
    });
  }

  static getError(key) {
    return errorStates.get(key);
  }

  static clearError(key) {
    errorStates.delete(key);
  }

  static hasError(key) {
    return errorStates.has(key);
  }

  static getAllErrors() {
    const errors = {};
    for (const [key, error] of errorStates.entries()) {
      errors[key] = error;
    }
    return errors;
  }
}

/**
 * Core metadata loading functions
 */

/**
 * Load a single metadata file
 * @param {string} path - Path to the metadata file
 * @param {Object} options - Loading options
 * @returns {Promise<Object>} - Loaded metadata
 */
export async function loadMetadataFile(path, options = {}) {
  const {
    useCache = true,
    forceReload = false,
    timeout = 10000,
    retries = 3,
  } = options;

  // Normalize path
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const cacheKey = normalizedPath;

  // Clear error state when starting new load
  ErrorStateManager.clearError(cacheKey);

  // Return cached data if available and not forcing reload
  if (useCache && !forceReload && MetadataCache.has(cacheKey)) {
    return MetadataCache.get(cacheKey);
  }

  // Prevent duplicate loading
  if (LoadingStateManager.isLoading(cacheKey)) {
    // Wait for existing load to complete
    return new Promise((resolve, reject) => {
      const checkLoading = () => {
        if (!LoadingStateManager.isLoading(cacheKey)) {
          if (MetadataCache.has(cacheKey)) {
            resolve(MetadataCache.get(cacheKey));
          } else if (ErrorStateManager.hasError(cacheKey)) {
            const error = ErrorStateManager.getError(cacheKey);
            reject(new Error(error.message));
          } else {
            reject(new Error("Loading failed for unknown reason"));
          }
        } else {
          setTimeout(checkLoading, 100);
        }
      };
      setTimeout(checkLoading, 100);
    });
  }

  // Set loading state
  LoadingStateManager.setLoading(cacheKey, true);

  try {
    // Construct full URL
    const baseUrl = window.location.origin;
    const fullUrl = `${baseUrl}${normalizedPath}`;

    // Load data with retry logic
    const data = await MetadataHTTPClient.fetchWithRetry(
      fullUrl,
      { timeout },
      retries
    );

    // Cache the result
    if (useCache) {
      MetadataCache.set(cacheKey, data);
    }

    return data;
  } catch (error) {
    // Store error state
    ErrorStateManager.setError(cacheKey, error);
    throw error;
  } finally {
    // Clear loading state
    LoadingStateManager.setLoading(cacheKey, false);
  }
}

/**
 * Load multiple metadata files
 * @param {string[]} paths - Array of file paths
 * @param {Object} options - Loading options
 * @returns {Promise<Object>} - Object with path -> data mappings
 */
export async function loadMetadataFiles(paths, options = {}) {
  const { parallel = true, failFast = false, ...loadOptions } = options;

  if (parallel) {
    // Load all files in parallel
    const promises = paths.map(async (path) => {
      try {
        const data = await loadMetadataFile(path, loadOptions);
        return { path, data, success: true };
      } catch (error) {
        if (failFast) {
          throw error;
        }
        return { path, error, success: false };
      }
    });

    const results = await Promise.all(promises);

    // Process results
    const success = {};
    const errors = {};

    for (const result of results) {
      if (result.success) {
        success[result.path] = result.data;
      } else {
        errors[result.path] = result.error;
      }
    }

    return { success, errors };
  } else {
    // Load files sequentially
    const success = {};
    const errors = {};

    for (const path of paths) {
      try {
        const data = await loadMetadataFile(path, loadOptions);
        success[path] = data;
      } catch (error) {
        errors[path] = error;
        if (failFast) {
          throw error;
        }
      }
    }

    return { success, errors };
  }
}

/**
 * Load metadata by category (templates, fields, datasets, enrichments)
 * @param {string} category - Metadata category
 * @param {string} filename - Specific filename (optional)
 * @param {Object} options - Loading options
 * @returns {Promise<Object>} - Loaded metadata
 */
export async function loadMetadataByCategory(
  category,
  filename = null,
  options = {}
) {
  if (!METADATA_PATHS[category]) {
    throw new Error(`Unknown metadata category: ${category}`);
  }

  const basePath = METADATA_PATHS[category];

  if (filename) {
    // Load specific file
    const filePath = `${basePath}/${filename}`;
    return loadMetadataFile(filePath, options);
  } else {
    // This would require a directory listing - for now, throw an error
    // In a real implementation, you might have an index file listing all available files
    throw new Error(
      `Directory listing not supported. Please specify a filename for category: ${category}`
    );
  }
}

/**
 * Load all core metadata (templates, fields, enrichments)
 * @param {Object} options - Loading options
 * @returns {Promise<Object>} - All loaded metadata
 */
export async function loadAllMetadata(options = {}) {
  const coreFiles = [
    "/metadata/templates/project-basic-v1.json",
    "/metadata/fields/project-fields.json",
    "/metadata/templates/deal-basic-v1.json",
    "/metadata/fields/deal-fields.json",
    "/metadata/templates/valuation-basic-v1.json",
    "/metadata/fields/valuation-fields.json",
    "/metadata/enrichments/companies-house.json",
  ];

  const { success, errors } = await loadMetadataFiles(coreFiles, options);

  // Organize by category
  const metadata = {
    templates: {},
    fields: {},
    enrichments: {},
    errors: {},
  };

  // Process successful loads
  for (const [path, data] of Object.entries(success)) {
    if (path.includes("/templates/")) {
      const filename = path.split("/").pop().replace(".json", "");
      metadata.templates[filename] = data;
    } else if (path.includes("/fields/")) {
      const filename = path.split("/").pop().replace(".json", "");
      metadata.fields[filename] = data;
    } else if (path.includes("/enrichments/")) {
      const filename = path.split("/").pop().replace(".json", "");
      metadata.enrichments[filename] = data;
    }
  }

  // Include any errors
  metadata.errors = errors;

  return metadata;
}

/**
 * Preload critical metadata for faster initial load
 * @param {Object} options - Loading options
 * @returns {Promise<void>}
 */
export async function preloadMetadata(options = {}) {
  try {
    await loadAllMetadata({
      useCache: true,
      parallel: true,
      failFast: false,
      ...options,
    });
  } catch (error) {
    console.warn("Metadata preload failed:", error);
    // Don't throw - preloading is optional
  }
}

/**
 * Utility functions
 */

/**
 * Get loading states for all metadata
 * @returns {Object} - Loading states
 */
export function getLoadingStates() {
  LoadingStateManager.clearExpiredLoading();
  return LoadingStateManager.getLoadingStates();
}

/**
 * Get error states for all metadata
 * @returns {Object} - Error states
 */
export function getErrorStates() {
  return ErrorStateManager.getAllErrors();
}

/**
 * Get cache statistics
 * @returns {Object} - Cache stats
 */
export function getCacheStats() {
  return MetadataCache.getStats();
}

/**
 * Clear all cached metadata
 * @param {string} pattern - Optional pattern to match keys (simple string matching)
 */
export function clearCache(pattern = null) {
  if (pattern) {
    // Clear specific pattern
    for (const key of metadataCache.keys()) {
      if (key.includes(pattern)) {
        MetadataCache.delete(key);
      }
    }
  } else {
    // Clear all
    MetadataCache.clear();
  }
}

/**
 * Refresh metadata (clear cache and reload)
 * @param {string|string[]} paths - Specific paths to refresh (optional)
 * @param {Object} options - Loading options
 * @returns {Promise<Object>} - Refreshed metadata
 */
export async function refreshMetadata(paths = null, options = {}) {
  if (paths) {
    const pathArray = Array.isArray(paths) ? paths : [paths];

    // Clear cache for specific paths
    for (const path of pathArray) {
      MetadataCache.delete(path);
    }

    // Reload specific paths
    return loadMetadataFiles(pathArray, { forceReload: true, ...options });
  } else {
    // Clear all and reload
    MetadataCache.clear();
    return loadAllMetadata({ forceReload: true, ...options });
  }
}

/**
 * Check if metadata is available (cached or can be loaded)
 * @param {string} path - Metadata file path
 * @returns {boolean} - Whether metadata is available
 */
export function isMetadataAvailable(path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return (
    MetadataCache.has(normalizedPath) &&
    !ErrorStateManager.hasError(normalizedPath)
  );
}

// Export the service interface
export default {
  loadMetadataFile,
  loadMetadataFiles,
  loadMetadataByCategory,
  loadAllMetadata,
  preloadMetadata,
  getLoadingStates,
  getErrorStates,
  getCacheStats,
  clearCache,
  refreshMetadata,
  isMetadataAvailable,
};
